//src/components/Navbar.js

import React, { useState, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { auth } from '../config/firebaseConfig';
import { signOut } from 'firebase/auth';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false); 
  const { t, i18n } = useTranslation();
  
  const { currentUser, userData } = useAuth(); 
  const navigate = useNavigate();

  const firstName = (userData?.displayName || currentUser?.displayName || '').split(' ')[0];

  const navItems = [
    { to: "/", icon: "bi-house", textKey: "Homepage", end: true },
    { to: "/community", icon: "bi-people", textKey: "Community" },
    { to: "/self-publishing-service", icon: "bi-pen", textKey: "Services" },
    { to: "/corporate", icon: "bi-briefcase", textKey: "Corporate" },
    { to: "/meet-sajeda", icon: "bi-person-heart", textKey: "MeetSajeda" },
    { to: "/contact", icon: "bi-envelope", textKey: "Contact" },
  ];

  const languages = [
    { code: 'en', name: 'English', displayName: 'EN' },
    { code: 'tr', name: 'Türkçe', displayName: 'TR' },
    { code: 'ar', name: 'العربية', displayName: 'AR' },
    { code: 'de', name: 'Deutsch', displayName: 'DE' },
    { code: 'fr', name: 'Français', displayName: 'FR' },
    { code: 'id', name: 'Bahasa Indonesia', displayName: 'ID' },
    { code: 'ru', name: 'Русский', displayName: 'RU' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
        setIsUserMenuOpen(false);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    if (isMobileMenuOpen) {
      closeMobileMenu();
    }
  };

  const handleLogout = async () => {
    try {
        await signOut(auth);
        navigate('/');
        closeMobileMenu(); 
    } catch (error) {
        console.error("Failed to log out:", error);
    }
  };

  const currentLanguageDisplayName = languages.find(lang => lang.code === i18n.language)?.displayName || i18n.language.toUpperCase();

  return (
    <>
      <style>{`
        @media (min-width: 992px) {
          .nav-links .custom-nav-link,
          .language-switcher .custom-nav-link,
          .custom-navbar-wrapper .custom-nav-link {
            font-size: 0.85rem !important;
            font-weight: 600 !important;
            white-space: nowrap;
            transition: all 0.3s ease;
          }

          /* SAYFA EN ÜSTTEYKEN -> Siyah Metin & Hafif Siyah Gölge */
          .custom-navbar-wrapper:not(.scrolled) .custom-nav-link,
          .custom-navbar-wrapper:not(.scrolled) .custom-nav-link i {
            color: #000000 !important;
            /* İstediğin düşük opaklıklı siyah gölge */
            
          }
          
          .custom-navbar-wrapper:not(.scrolled) .btn-outline-dark {
            color: #000000 !important;
            border-color: #000000 !important;
          }

          /* SAYFA AŞAĞI KAYDIRILDIĞINDA */
          .custom-navbar-wrapper.scrolled .custom-nav-link,
          .custom-navbar-wrapper.scrolled .custom-nav-link i {
            color: #1a1a1a !important;
            text-shadow: none !important;
          }
          
          .custom-navbar-wrapper.scrolled .btn-outline-dark {
            color: #1a1a1a !important;
            border-color: #1a1a1a !important;
          }
        }
      `}</style>

      <header className={`custom-navbar-wrapper ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <nav className="d-flex align-items-center justify-content-between">
            <Link to="/" className="navbar-brand">
              <img
                src={`${process.env.PUBLIC_URL}/images/logo-new.png`}
                alt="Little Hibba Logo"
                className="navbar-logo"
              />
            </Link>

            <div className="d-none d-lg-flex align-items-center">
              <nav className="nav-links d-flex align-items-center gap-3">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className="custom-nav-link d-flex align-items-center gap-2"
                    end={item.end}
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <span>{t(`navbar.${item.textKey}`)}</span>
                  </NavLink>
                ))}
              </nav>

              <div className="d-flex align-items-center gap-3 ms-4">
                <Dropdown as="div" className="language-switcher">
                  <Dropdown.Toggle variant="link" id="dropdown-languages" className="custom-nav-link p-0 d-flex align-items-center gap-2">
                    <i className="bi bi-translate fs-5" />
                    <span>{currentLanguageDisplayName}</span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    {languages.map((lang) => (
                      <Dropdown.Item key={lang.code} onClick={() => handleLanguageChange(lang.code)} active={i18n.language === lang.code}>
                        {lang.name} <span className="text-muted">({lang.displayName})</span>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {currentUser ? (
                    <Dropdown as="div">
                      <Dropdown.Toggle variant="link" id="dropdown-user" className="custom-nav-link p-0 d-flex align-items-center gap-2">
                        <i className="bi bi-person-circle fs-5"></i>
                        <span>{t('navbar.Welcome', { name: firstName || currentUser.email })}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end">
                        <Dropdown.Item onClick={() => navigate('/profile')}>My Profile</Dropdown.Item>
                        {userData?.role === 'admin' && (
                            <Dropdown.Item onClick={() => navigate('/admin')}>Admin Panel</Dropdown.Item>
                        )}
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                ) : (
                  <div className='d-flex align-items-center gap-2'>
                    <Link to="/login" className="btn btn-outline-dark rounded-pill btn-sm px-3">
                      <span>{t('navbar.Login')}</span>
                    </Link>
                    <Link to="/signup" className="btn btn-dark rounded-pill btn-sm px-3">
                      <span>{t('navbar.SignUp')}</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <button className={`hamburger-button d-lg-none ${isMobileMenuOpen ? 'is-active' : ''}`} onClick={toggleMobileMenu} aria-label="Toggle navigation">
              <span className="line"></span>
              <span className="line"></span>
            </button>
          </nav>
        </div>
      </header>

      <div className={`mobile-menu-modal ${isMobileMenuOpen ? 'is-active' : ''}`}>
        <div className="mobile-menu-content p-4 d-flex flex-column">
          <nav className="mobile-nav-links">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className="custom-nav-link d-flex align-items-center gap-2"
                end={item.end}
                onClick={closeMobileMenu}
              >
                <i className={`bi ${item.icon}`}></i>
                <span>{t(`navbar.${item.textKey}`)}</span>
              </NavLink>
            ))}

            <hr className="my-2" />

            {currentUser ? (
              <>
                <div 
                  className="custom-nav-link d-flex align-items-center justify-content-between" 
                  onClick={toggleUserMenu}
                  role="button"
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-person-circle"></i>
                    <span>{t('navbar.Welcome', { name: firstName || currentUser.email })}</span>
                  </div>
                  <i className={`bi ${isUserMenuOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                </div>
                
                {isUserMenuOpen && (
                  <div className="d-flex flex-column ps-4 mt-2">
                    <Link to="/profile" className="custom-nav-link d-flex align-items-center gap-2" onClick={closeMobileMenu}>
                      <i className="bi bi-person-fill"></i>
                      <span>My Profile</span>
                    </Link>
                    {userData?.role === 'admin' && (
                      <Link to="/admin" className="custom-nav-link d-flex align-items-center gap-2" onClick={closeMobileMenu}>
                        <i className="bi bi-shield-lock-fill"></i>
                        <span>Admin Panel</span>
                      </Link>
                    )}
                    <div className="custom-nav-link d-flex align-items-center gap-2" onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right"></i>
                      <span>Logout</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <Link to="/login" className="custom-nav-link d-flex align-items-center gap-2" onClick={closeMobileMenu}>
                  <i className="bi bi-box-arrow-in-right"></i>
                  <span>{t('navbar.Login')}</span>
                </Link>
                <Link to="/signup" className="custom-nav-link d-flex align-items-center gap-2" onClick={closeMobileMenu}>
                  <i className="bi bi-person-plus-fill"></i>
                  <span>{t('navbar.SignUp')}</span>
                </Link>
              </>
            )}
          </nav>
          
          <div className="mobile-menu-footer mt-auto">
            <div className="language-switcher-mobile">
              {languages.map(lang => (
                <button
                  key={lang.code}
                  className={`lang-button ${i18n.language === lang.code ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  {lang.displayName}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;